import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Collapse from "@mui/material/Collapse";

import ConditionalSlider from "./ConditionalSlider";
import { getType, isConditionalQuestion, subTypes, types } from "../itemTypes";
import ExpandButton from "./ExpandButton";
import DefaultSliderValue from "./DefaultSliderValue";
import CheckBoxList from "./CheckBoxList";
import RadioList from "./RadioList";
import getChoiceOption from "../Choice/getChoiceOption";

export default function ConditionalDialog(props) {
  const { t } = useTranslation();
  const { open, onClose, handleSave, parentQuestion, childQuestion } = props;
  const [conditions, setConditions] = React.useState(null);
  const [defaultValue, setDefaultValue] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false); // show more option in dialog
  const [isFormValid, setIsFormValid] = React.useState(true); // allows submition of the conditional dialog
  const isConditionalAllowed = isConditionalQuestion(parentQuestion?.type);

  const handleConditions = (c) => setConditions(c);

  const handleDefaultValue = (value) => setDefaultValue(value);

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  const handleFormValidation = (bool) => {
    setIsFormValid(bool);
  };

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    // init conditions
    const parentType = parentQuestion.type;
    switch (getType(parentType)) {
      case types.choice:
        setConditions(childQuestion?.conditions ?? []);
        break;
      case types.slider:
        break;
      default:
        setConditions(null);
        break;
    }

    // init defaultValue
    const childType = childQuestion.type;
    switch (getType(childType)) {
      case types.choice:
        setDefaultValue(childQuestion?.defaultValue ?? -1);
        break;
      case types.slider:
        setDefaultValue(childQuestion?.defaultValue ?? null);
        break;
      default:
        setDefaultValue(null);
        break;
    }
  }, [parentQuestion, childQuestion]);

  // show the dialog title
  const renderDialogTitle = () => {
    const childType = parentQuestion.type;
    switch (getType(childType)) {
      case types.choice:
        return t("editor.conditionalDialog.choice");
      case types.slider:
        return t("editor.conditionalDialog.slider");
      default:
        return "";
    }
  };

  // render the right condition component
  const renderConditionalType = () => {
    const parentType = parentQuestion.type;
    switch (getType(parentType)) {
      case types.choice:
        const checkBoxes = ![subTypes.yesno, subTypes.computation].includes(
          parentQuestion.type
        );
        if (checkBoxes) {
          return (
            <CheckBoxList
              options={parentQuestion.options}
              checked={conditions}
              setChecked={handleConditions}
            />
          );
        } else {
          return (
            <RadioList
              options={parentQuestion.options}
              checked={conditions}
              setChecked={handleConditions}
            />
          );
        }

      case types.slider:
        return (
          <ConditionalSlider
            handleConditions={handleConditions}
            initInterval={
              childQuestion?.conditions?.length > 0
                ? childQuestion.conditions
                : parentQuestion.marks
            }
            boundaries={parentQuestion.marks}
            handleFormValidation={handleFormValidation}
          />
        );

      default:
        return (
          <React.Fragment>
            <Typography variant="h6">
              Cannot make a conditional question
            </Typography>
            <Typography>The previous question is not valid</Typography>
          </React.Fragment>
        );
    }
  };

  // render the right default value component
  const renderDefaulltValue = () => {
    const childType = childQuestion.type;
    switch (getType(childType)) {
      case types.slider:
        return (
          <DefaultSliderValue
            boundaries={childQuestion.marks}
            defaultValue={defaultValue}
            handleDefaultValue={handleDefaultValue}
            handleFormValidation={handleFormValidation}
          />
        );

      case types.choice:
        const options = [
          getChoiceOption({
            title: t("editor.conditionalDialog.skip"),
            number: -1,
          }),
          ...childQuestion.options,
        ];
        const checked =
          defaultValue === -1
            ? [options[0].id]
            : options?.[defaultValue]?.id
            ? [options[defaultValue].id]
            : [];
        const handleChecked = (idArray) => {
          if (options[0].id === idArray[0]) {
            handleDefaultValue(-1);
          } else {
            const index = options.findIndex(
              (option) => option.id === idArray[0]
            );
            handleDefaultValue(index);
          }
        };

        return (
          <Box>
            <Typography>
              {t("editor.conditionalDialog.defaultValue")}
            </Typography>
            <RadioList
              options={options}
              checked={checked}
              setChecked={handleChecked}
            />
          </Box>
        );

      default:
        return <div></div>;
    }
  };

  return (
    <Dialog
      //disableBackdropClick
      //disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={{ maxWidth: "sm", margin: "auto" }}
      aria-labelledby="condition-dialog-title"
      open={open}
      onClose={handleCancel}
    >
      <DialogTitle id="condtitions-dialog-title">
        {renderDialogTitle()}
      </DialogTitle>
      <DialogContent>
        {renderConditionalType()}
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          sx={{ pt: 2, pb: 2 }}
        >
          {renderDefaulltValue()}
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        {isConditionalAllowed ? (
          <Box>
            <ExpandButton
              expanded={expanded}
              handleExpandClick={handleExpandClick}
            />
          </Box>
        ) : null}
        <Box>
          <Button onClick={handleCancel} color="inherit">
            {t("common.actions.cancel")}
          </Button>
          {isConditionalAllowed ? (
            <Button
              onClick={() =>
                handleSave({
                  conditions: conditions,
                  defaultValue: defaultValue,
                })
              }
              color="primary"
              disabled={!isFormValid}
            >
              {t("common.actions.ok")}
            </Button>
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ConditionalDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  parentQuestion: PropTypes.object.isRequired,
  childQuestion: PropTypes.object.isRequired,
};
